import React from "react";
import ReactDOM from "react-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { createOrganization } from "../../services/organizationService";
import { toast } from "../../hooks/use-toast";
import { Building, X } from "lucide-react";

const formSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters." }),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateOrganizationDialogProps {
  onSuccess?: (organizationId: string) => void;
  trigger?: React.ReactNode;
  buttonText?: string;
  tenantType?: 'personal' | 'organisation';
  dialogTitle?: string;
  dialogDescription?: string;
}

export function CreateOrganizationDialog({
  onSuccess,
  trigger,
  buttonText = "Create Organization",
  tenantType = 'organisation',
  dialogTitle = "Create Organization",
  dialogDescription = "Set up a new workspace",
}: CreateOrganizationDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    console.log('[CreateOrganizationDialog] Open state:', open);
  }, [open]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  async function onSubmit(values: FormValues) {
    setIsLoading(true);
    try {
      const org = await createOrganization(values.name, values.description, tenantType);

      if (org) {
        const entityName = tenantType === 'personal' ? 'Workspace' : 'Organization';
        toast({
          title: `${entityName} created`,
          description: `${org.name} has been created successfully.`,
        });

        if (onSuccess) {
          onSuccess(org.id);
        }

        setOpen(false);
        form.reset();
      }
    } catch (error) {
      console.error("Error creating organization:", error);
      const entityName = tenantType === 'personal' ? 'workspace' : 'organization';
      toast({
        title: "Error",
        description: `Failed to create ${entityName}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  // Modal content component
  const modalContent = open ? (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={() => setOpen(false)}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#dbeafe'
            }}>
              <Building style={{ width: '20px', height: '20px', color: '#2563eb' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#111827' }}>
                {dialogTitle}
              </h2>
              <p style={{ fontSize: '14px', margin: 0, color: '#6b7280', marginTop: '2px' }}>
                {dialogDescription}
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X style={{ width: '16px', height: '16px', color: '#6b7280' }} />
          </button>
        </div>

        {/* Form Content */}
        <div style={{ padding: '24px', maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      Organization Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Acme Corporation"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      Description <span style={{ color: '#9ca3af', fontWeight: '400' }}>(Optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of your organization..."
                        style={{ minHeight: '80px', resize: 'none' }}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '20px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            onClick={form.handleSubmit(onSubmit)}
            style={{ minWidth: '100px' }}
          >
            {isLoading ? "Creating..." : "Create"}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Direct button */}
      {trigger ? (
        <div onClick={() => setOpen(true)}>{trigger}</div>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            console.log('[CreateOrganizationDialog] Manual open clicked');
            setOpen(true);
          }}
        >
          <Building className="w-4 h-4 mr-2" />
          {buttonText}
        </Button>
      )}

      {/* Render modal in portal to document.body for global visibility */}
      {typeof document !== 'undefined' && modalContent && ReactDOM.createPortal(
        modalContent,
        document.body
      )}
    </>
  );
}
